// ─── Table Types ─────────────────────────────────────────────────────────────

export type CellType =
  | "text"
  | "imageAndText"
  | "tag"
  | "tagGroup"
  | "input"
  | "quantityPicker"
  | "checkbox"
  | "icon"
  | "button"
  | "link";

export interface Column {
  id: string;
  label: string;
  cellType: CellType;
  width: number;
  key: string;
  bold: boolean;
  /** Override horizontal padding (px) for this column */
  paddingLeft?: number;
  paddingRight?: number;
  /** When set, renders an info icon next to the header label with this text on hover. */
  headerTooltip?: string;
  /** Sticks this column to the left edge while the table scrolls horizontally. Intended for a single leading column (e.g. a thumbnail). */
  pinned?: boolean;
}

export interface RowAction {
  id: string;
  label: string;
  display: "inline" | "overflow";
  variant: "primary" | "secondary" | "tertiary" | "plain";
}

/** Render-only config — what SlateTable needs to render a table */
export interface TableConfig {
  columns: Column[];
  cellValues: Record<string, string>;
  imageUrlValues: Record<string, string>;
  subtextValues: Record<string, string>;
  rowCount: number;
  rowsSelectable: boolean;
  rowActionsEnabled: boolean;
  rowActionItems: RowAction[];
  columnPrompts: Record<string, string>;
}

/** Full saved config — includes metadata for persistence */
export interface SavedTableConfig extends TableConfig {
  id: string;
  name: string;
  savedAt: string;
  /** Stable key for auto-save/load within prototypes */
  tableId?: string;
}

export interface SlateTableRow {
  id: string;
  text: string;
  tag: string;
  tagGroup: string[];
  imageAndText: { url: string; name: string; subtext: string };
  input: string;
  quantityPicker: number;
  checkbox: boolean;
  icon: string;
  button: string;
  link: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

export const CELL_TYPE_KEY_MAP: Record<CellType, string> = {
  text: "text",
  imageAndText: "imageAndText",
  tag: "tag",
  tagGroup: "tagGroup",
  input: "input",
  quantityPicker: "quantityPicker",
  checkbox: "checkbox",
  icon: "icon",
  button: "button",
  link: "link",
};

export const CELL_TYPE_LABELS: Record<CellType, string> = {
  text: "Text",
  imageAndText: "Image + Text",
  tag: "Tag",
  tagGroup: "Tag Group",
  input: "Input",
  quantityPicker: "Quantity Picker",
  checkbox: "Checkbox",
  icon: "Icon",
  link: "Link",
  button: "Button",
};

export const WIDTH_PRESETS = [
  { label: "XS", value: 80 },
  { label: "S", value: 120 },
  { label: "M", value: 180 },
  { label: "L", value: 250 },
  { label: "XL", value: 350 },
];

export const CELL_TYPE_OPTIONS = (
  Object.keys(CELL_TYPE_LABELS) as CellType[]
).map((t) => ({
  value: t,
  label: CELL_TYPE_LABELS[t],
}));

export const WIDTH_OPTIONS = WIDTH_PRESETS.map((p) => ({
  value: p.value.toString(),
  label: p.label,
}));

export const DEFAULT_COLUMNS: Column[] = [
  {
    id: "col-1",
    label: "Name",
    cellType: "text",
    width: 180,
    key: "text",
    bold: false,
  },
  {
    id: "col-2",
    label: "Status",
    cellType: "tag",
    width: 120,
    key: "tag",
    bold: false,
  },
  {
    id: "col-3",
    label: "Tags",
    cellType: "tagGroup",
    width: 180,
    key: "tagGroup",
    bold: false,
  },
];

export const DEFAULT_ROW_ACTIONS: RowAction[] = [
  { id: "ra-1", label: "Edit", display: "overflow", variant: "secondary" },
  { id: "ra-2", label: "Duplicate", display: "overflow", variant: "secondary" },
  { id: "ra-3", label: "Delete", display: "overflow", variant: "secondary" },
];

export const MOCK_ROWS: SlateTableRow[] = [
  {
    id: "r1",
    text: "Pro Plan",
    tag: "Active",
    tagGroup: ["Design"],
    imageAndText: {
      url: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=80&h=80&fit=crop",
      name: "Packaging Kit",
      subtext: "",
    },
    input: "alice@example.com",
    quantityPicker: 3,
    checkbox: true,
    icon: "check",
    button: "View Details",
    link: "View details",
  },
  {
    id: "r2",
    text: "Starter",
    tag: "Pending",
    tagGroup: ["Dev", "QA"],
    imageAndText: {
      url: "https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?w=80&h=80&fit=crop",
      name: "Wireless Charger",
      subtext: "",
    },
    input: "bob@example.com",
    quantityPicker: 1,
    checkbox: false,
    icon: "x",
    button: "Review",
    link: "Review",
  },
  {
    id: "r3",
    text: "Enterprise",
    tag: "Active",
    tagGroup: ["Marketing"],
    imageAndText: {
      url: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=80&h=80&fit=crop",
      name: "Oak Desk",
      subtext: "",
    },
    input: "carol@example.com",
    quantityPicker: 7,
    checkbox: true,
    icon: "check",
    button: "Upgrade",
    link: "Upgrade plan",
  },
  {
    id: "r4",
    text: "Business",
    tag: "Inactive",
    tagGroup: ["Design", "Dev"],
    imageAndText: {
      url: "https://images.unsplash.com/photo-1524578271613-d550eacf6090?w=80&h=80&fit=crop",
      name: "Brand Guide",
      subtext: "",
    },
    input: "dan@example.com",
    quantityPicker: 2,
    checkbox: true,
    icon: "alertCircle",
    button: "Convert",
    link: "Convert",
  },
  {
    id: "r5",
    text: "Free Tier",
    tag: "Pending",
    tagGroup: ["Analytics"],
    imageAndText: {
      url: "https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=80&h=80&fit=crop",
      name: "Logo Tee",
      subtext: "",
    },
    input: "eve@example.com",
    quantityPicker: 5,
    checkbox: false,
    icon: "x",
    button: "Manage",
    link: "Manage",
  },
  {
    id: "r6",
    text: "Growth",
    tag: "Active",
    tagGroup: ["Dev"],
    imageAndText: {
      url: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=80&h=80&fit=crop",
      name: "Sample Box",
      subtext: "",
    },
    input: "frank@example.com",
    quantityPicker: 4,
    checkbox: true,
    icon: "check",
    button: "Activate",
    link: "Activate",
  },
  {
    id: "r7",
    text: "Premium",
    tag: "Active",
    tagGroup: ["Design", "Marketing"],
    imageAndText: {
      url: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=80&h=80&fit=crop",
      name: "Running Shoe",
      subtext: "",
    },
    input: "grace@example.com",
    quantityPicker: 6,
    checkbox: true,
    icon: "check",
    button: "Renew",
    link: "Renew",
  },
  {
    id: "r8",
    text: "Trial",
    tag: "Pending",
    tagGroup: ["QA"],
    imageAndText: {
      url: "https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=80&h=80&fit=crop",
      name: "Sunglasses",
      subtext: "",
    },
    input: "henry@example.com",
    quantityPicker: 1,
    checkbox: false,
    icon: "alertCircle",
    button: "Extend",
    link: "Extend trial",
  },
  {
    id: "r9",
    text: "Agency",
    tag: "Active",
    tagGroup: ["Dev", "Design"],
    imageAndText: {
      url: "https://images.unsplash.com/photo-1585386959984-a4155224a1ad?w=80&h=80&fit=crop",
      name: "Watch",
      subtext: "",
    },
    input: "iris@example.com",
    quantityPicker: 10,
    checkbox: true,
    icon: "check",
    button: "Configure",
    link: "Configure",
  },
  {
    id: "r10",
    text: "Basic",
    tag: "Inactive",
    tagGroup: ["Analytics"],
    imageAndText: {
      url: "https://images.unsplash.com/photo-1560343090-f0409e92791a?w=80&h=80&fit=crop",
      name: "Backpack",
      subtext: "",
    },
    input: "jack@example.com",
    quantityPicker: 0,
    checkbox: false,
    icon: "x",
    button: "Reactivate",
    link: "Reactivate",
  },
  {
    id: "r11",
    text: "Scale",
    tag: "Active",
    tagGroup: ["Marketing", "QA"],
    imageAndText: {
      url: "https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=80&h=80&fit=crop",
      name: "Sneaker",
      subtext: "",
    },
    input: "kate@example.com",
    quantityPicker: 8,
    checkbox: true,
    icon: "check",
    button: "Details",
    link: "Details",
  },
  {
    id: "r12",
    text: "Lite",
    tag: "Pending",
    tagGroup: ["Dev"],
    imageAndText: {
      url: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=80&h=80&fit=crop",
      name: "Headphones",
      subtext: "",
    },
    input: "leo@example.com",
    quantityPicker: 2,
    checkbox: false,
    icon: "alertCircle",
    button: "Upgrade",
    link: "Upgrade",
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function genColumnId() {
  return "col-" + Math.random().toString(36).slice(2, 8);
}

export function genActionId() {
  return "ra-" + Math.random().toString(36).slice(2, 8);
}

export function genRowId() {
  return "r-" + Math.random().toString(36).slice(2, 8);
}

export function createPlaceholderRow(id: string): SlateTableRow {
  return {
    id,
    text: "",
    tag: "",
    tagGroup: [],
    imageAndText: {
      url: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=80&h=80&fit=crop",
      name: "",
      subtext: "",
    },
    input: "",
    quantityPicker: 0,
    checkbox: false,
    icon: "check",
    button: "",
    link: "",
  };
}

export const DEFAULT_TABLE_CONFIG: TableConfig = {
  columns: DEFAULT_COLUMNS,
  cellValues: {},
  imageUrlValues: {},
  subtextValues: {},
  rowCount: 12,
  rowsSelectable: true,
  rowActionsEnabled: true,
  rowActionItems: DEFAULT_ROW_ACTIONS,
  columnPrompts: {},
};
